<template>
  <div class="promocode-campaigns" v-loading="isCampaignsLoading">
    <div class="d-flex fl-s-b header">
      <p class="title">{{ $t("promocode.pinned-campaigns") }}</p>
      <SButton
        type="button"
        variant="outline"
        size="lg"
        class="show-all-btn"
        @click="openAllCampaignsModal"
      >
        <SvgIcon
          :iconContent="CouponIcon"
          iconAlt=""
          iconStyle="width: 30px;
        height: 30px; padding-right: 10px;"
        />
        {{ $t("button.see-all") }}
      </SButton>
    </div>
    <div class="campaigns-grid">
      <div
        v-for="card in cards"
        :key="card.id"
        @click="toggleApplyPromocode(card)"
      >
        <PromocodeCampaignCard :data="card" />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, watch, ref } from "vue";
import { useI18n } from "vue-i18n";
import CouponIcon from "@/assets/images/svg/coupon.svg";
import {
  IPromocodeCampaign,
  IPromocodeCardData,
} from "@/types/db/promocode-campaign";
import { getActivePromocodeCampaigns } from "@/db/promocode-campaign";
import ModalModule from "@/store/modules/modal";
import { ModalStatus } from "@/constants/modal";
import { DiscountType } from "@smeetz/order-common";
import { useOrderStore } from "@smeetz/order-store";
import { removeDiscount } from "@/utils/vue-helpers/orders.helper";
import { SButton } from "@smeetz/ui-core";

export default defineComponent({
  name: "promocode-campaigns",
  components: { SButton },
  setup() {
    const { t } = useI18n({});
    const orderStore = useOrderStore();
    const cards = ref<IPromocodeCardData[]>([]);
    const promocodeCampaigns = ref<IPromocodeCampaign[]>([]);
    // Promocode campaigns only deal with PROMOCODE-style discounts —
    // vouchers (gift cards) live in `orderSummary.vouchers` and don't apply
    // here.
    const appliedCodes = computed(() =>
      (orderStore.selectedOrder?.orderSession?.orderDiscounts ?? []).filter(
        (od) => od.type !== DiscountType.VOUCHER
      )
    );
    const isCampaignsLoading = ref(false);

    function openAllCampaignsModal() {
      ModalModule.setStatus(ModalStatus.PromocodeCampaigns);
    }

    async function toggleApplyPromocode(data: IPromocodeCardData) {
      if (data.isApplied) {
        isCampaignsLoading.value = true;
        try {
          const discountId = appliedCodes.value.find(
            (discount) => String(discount.code) === data.code
          )?.id;
          if (discountId) {
            await removeDiscount(String(discountId));
          }
        } catch {
          // journaled + surfaced by the order helper; the code stays applied
        } finally {
          isCampaignsLoading.value = false;
        }
      } else {
        ModalModule.setModalProps({ props: data });
        ModalModule.setStatus(ModalStatus.PromocodeCampaignsTickets);
      }
    }

    async function loadPinnedCampaings() {
      // load all pinned campaigns
      promocodeCampaigns.value = await getActivePromocodeCampaigns({
        pinnedOnly: true,
      });
      updateCardsList();
    }

    function updateCardsList() {
      // load only pinned campaigns
      cards.value = promocodeCampaigns.value.map((campaign) => {
        const code = campaign.codes[0];
        const isApplied = appliedCodes.value.some(
          (discount) => String(discount.code) === code
        );
        return {
          id: campaign.id,
          code,
          name: campaign.name,
          picture: campaign.picture,
          flatValue: campaign.flatValue,
          percentageValue: campaign.percentageValue,
          isApplied: !!isApplied,
        } as IPromocodeCardData;
      });
    }

    loadPinnedCampaings();
    watch(appliedCodes, () => {
      updateCardsList();
    });

    return {
      t,
      CouponIcon,
      openAllCampaignsModal,
      cards,
      toggleApplyPromocode,
      isCampaignsLoading,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;
$btns-height: 40px;

.promocode-campaigns {
  height: 90%;
  .header {
    padding: 1em 0;
    font-size: 16px;
    .show-all-btn {
      height: 50px;
      border-radius: 4px;
      border: 1px solid rgba(28, 138, 148, 1);
    }
    .title {
      font-size: 24px;
      font-weight: 500;
    }
  }

  .campaigns-grid {
    width: 100%;
    height: 100%;
    max-height: 60vh;
    display: grid;
    grid-template-rows: repeat(auto-fill, 75px);
    grid-template-columns: repeat(auto-fill, 220px);
    gap: 10px;
    overflow-y: auto;
  }
}
</style>
